import axios, { AxiosResponse } from 'axios';
import { API_ROUTES as API } from '../constants/apiRoutes';
import { REACT_APP_BASE_URL, REACT_APP_ENGINE_BASE_URL } from '../config';

const axiosInstance = axios.create({
  baseURL: REACT_APP_BASE_URL
});

axiosInstance.interceptors.request.use(
  (config) => {
    const user_access_token = localStorage.getItem('user_access_token') || '';
    if (user_access_token) {
      config.headers.Authorization = `Bearer ${user_access_token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const message = error?.response?.data?.message || error?.message || 'Unknown error';

    if (status === 401) {
      // Trigger your session expired modal
      triggerSessionExpired();

      // Log or send a string, not the whole object
      console.error('Session expired:', message);
    }
  }
);

// session-expired-event.ts
export const triggerSessionExpired = () => {
  globalThis.dispatchEvent(new Event('SESSION_EXPIRED'));
  sessionStorage.clear();
  localStorage.clear();
};

const handleResponse = async <T>(request: Promise<AxiosResponse<T>>): Promise<T> => {
  try {
    const response = await request;
    return response.data;
  } catch (error) {
    console.error('Error:', error);
    throw error; // re-throw to handle it in the calling function if needed
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const printFormattedJSON = (jsonObject: any) => {
  return JSON.stringify(jsonObject, null, 2);
};

const getDob = (age: number) => {
  const today = new Date();
  const birthYear = today.getFullYear() - age;

  // Approximate DOB as the same day and month of the current date
  return new Date(birthYear, today.getMonth(), today.getDate());
};

const getAge = (dob: string) => {
  const birthDate = new Date(dob);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  // If the birth date hasn't occurred yet this year, subtract 1 from age
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
};

const calculateTimeAgo = (dateString: Date): string => {
  const date = new Date(dateString);
  const currentDate = new Date();
  const differenceInMilliseconds = currentDate.getTime() - date.getTime();

  // Time constants
  const millisecondsInSecond = 1000;
  const millisecondsInMinute = millisecondsInSecond * 60;
  const millisecondsInHour = millisecondsInMinute * 60;
  const millisecondsInDay = millisecondsInHour * 24;

  // Calculate differences in various units
  const differenceInDays = Math.floor(differenceInMilliseconds / millisecondsInDay);
  const differenceInHours = Math.floor(differenceInMilliseconds / millisecondsInHour);
  const differenceInMinutes = Math.floor(differenceInMilliseconds / millisecondsInMinute);
  const differenceInSeconds = Math.floor(differenceInMilliseconds / millisecondsInSecond);

  // Return formatted string based on the size of the difference
  if (differenceInDays > 0) {
    return `${differenceInDays} day${differenceInDays > 1 ? 's' : ''} ago`;
  } else if (differenceInHours > 0) {
    return `${differenceInHours} hour${differenceInHours > 1 ? 's' : ''} ago`;
  } else if (differenceInMinutes > 0) {
    return `${differenceInMinutes} minute${differenceInMinutes > 1 ? 's' : ''} ago`;
  } else {
    return `${differenceInSeconds} second${differenceInSeconds > 1 ? 's' : ''} ago`;
  }
};

const graphqlAxiosInstance = axios.create({
  baseURL: REACT_APP_ENGINE_BASE_URL
});

graphqlAxiosInstance.interceptors.request.use(
  (config) => {
    const user_access_token = localStorage.getItem('user_access_token') || '';
    if (user_access_token) {
      config.headers.Authorization = `Bearer ${user_access_token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => Promise.reject(error)
);

export {
  axiosInstance,
  handleResponse,
  API,
  printFormattedJSON,
  getDob,
  getAge,
  calculateTimeAgo,
  graphqlAxiosInstance
};
